import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  FileText, Settings, CheckCircle2, XCircle, RefreshCw, 
  Send, Download, Search, Filter, Clock, AlertTriangle,
  Building2, ArrowRight, Database, Zap, Lock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../../components/ui/table";
import {
  Alert, AlertDescription, AlertTitle
} from "../../../components/ui/alert";
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from "../../../components/ui/tabs";

const FacturacionElectronica = () => {
  const [activeTab, setActiveTab] = useState("emitidos");

  // Datos de ejemplo para mostrar la estructura
  const comprobantesEmitidos = [
    { id: 1, tipo: "FA-A", numero: "0001-00001234", fecha: "2026-03-01", cliente: "CLINICA OFTALMOLOGICA NORTE SRL", cuit: "30-77889944-2", total: 125000, cae: "74123456789012", estado: "aprobado" },
    { id: 2, tipo: "FA-A", numero: "0001-00001235", fecha: "2026-03-02", cliente: "CENTRO MEDICO VISUAL SA", cuit: "30-65432198-5", total: 85000, cae: "74123456789013", estado: "aprobado" },
    { id: 3, tipo: "NC-A", numero: "0001-00000089", fecha: "2026-03-03", cliente: "CLINICA OFTALMOLOGICA NORTE SRL", cuit: "30-77889944-2", total: -25000, cae: "74123456789014", estado: "aprobado" },
    { id: 4, tipo: "FA-B", numero: "0002-00000456", fecha: "2026-03-04", cliente: "MARTINEZ JUAN CARLOS", cuit: "20-12345678-9", total: 45000, cae: null, estado: "pendiente" },
  ];

  const comprobantesRecibidos = [
    { id: 1, tipo: "FA-A", numero: "0003-00005678", fecha: "2026-03-01", proveedor: "LABORATORIOS OPTICOS SA", cuit: "30-65432198-5", total: 180000, cae: "74987654321098", validado: true },
    { id: 2, tipo: "FA-A", numero: "0001-00002345", fecha: "2026-03-02", proveedor: "DISTRIBUIDORA MEDICA SRL", cuit: "30-71234567-8", total: 250000, cae: "74987654321099", validado: true },
    { id: 3, tipo: "FA-B", numero: "0005-00001111", fecha: "2026-03-03", proveedor: "IMPRENTA GRAFICA CENTRAL", cuit: "30-55667788-1", total: 35000, cae: "74987654321100", validado: false },
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(value);
  };

  const getEstadoBadge = (estado) => {
    switch(estado) {
      case "aprobado":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Aprobado</Badge>;
      case "pendiente":
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
      case "rechazado":
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rechazado</Badge>;
      default:
        return <Badge variant="outline">{estado}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="facturacion-electronica">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Facturación Electrónica</h1>
          <p className="text-gray-600">Integración con Smartboat - AFIP</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button className="bg-[#2853B9] hover:bg-[#1E3876]">
            <Send className="w-4 h-4 mr-2" />
            Emitir Comprobante
          </Button>
        </div>
      </div>

      {/* Alerta de configuración pendiente */}
      <Alert className="border-orange-200 bg-orange-50">
        <AlertTriangle className="w-4 h-4 text-orange-600" />
        <AlertTitle className="text-orange-800">Configuración Pendiente</AlertTitle>
        <AlertDescription className="text-orange-700">
          Este módulo se conecta con las <strong>APIs de Facturación Electrónica de Smartboat</strong>. 
          Se requiere configurar las credenciales de acceso para CAO para habilitar la emisión y recepción de comprobantes.
        </AlertDescription>
      </Alert>

      {/* Estado de conexión */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="cao-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Database className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">API Smartboat</p>
                <p className="font-semibold text-green-600">Disponible</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cao-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Credenciales CAO</p>
                <p className="font-semibold text-yellow-600">Pendiente</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cao-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">AFIP WSFE</p>
                <p className="font-semibold text-gray-400">Sin configurar</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cao-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <Zap className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Sincronización</p>
                <p className="font-semibold text-gray-400">Inactiva</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Funcionalidades */}
      <Card className="cao-card">
        <CardHeader>
          <CardTitle>Funcionalidades del Módulo</CardTitle>
          <CardDescription>
            Una vez configurado, tendrá acceso a las siguientes funcionalidades
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-[#1E3876] flex items-center gap-2">
                <Send className="w-4 h-4" />
                Emisión de Comprobantes
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Facturas A, B y C
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Notas de Crédito y Débito
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Recibos electrónicos
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Obtención automática de CAE
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Generación de PDF con código QR
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Envío automático por email
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold text-[#1E3876] flex items-center gap-2">
                <Download className="w-4 h-4" />
                Recepción y Validación
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Importación de comprobantes recibidos
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Validación de CAE contra AFIP
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Carga automática en Compras
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Generación de asiento contable
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Libro IVA Digital
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Exportación para DDJJ
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vista previa de comprobantes (mockup) */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="emitidos">Comprobantes Emitidos</TabsTrigger>
          <TabsTrigger value="recibidos">Comprobantes Recibidos</TabsTrigger>
          <TabsTrigger value="libros">Libros IVA</TabsTrigger>
        </TabsList>

        <TabsContent value="emitidos" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Comprobantes Emitidos (Vista Previa)</CardTitle>
                <div className="flex gap-2">
                  <Input placeholder="Buscar..." className="w-48" />
                  <Button variant="outline" size="icon">
                    <Filter className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Número</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>CUIT</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>CAE</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comprobantesEmitidos.map((comp) => (
                    <TableRow key={comp.id} className="opacity-60">
                      <TableCell>
                        <Badge variant="outline">{comp.tipo}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{comp.numero}</TableCell>
                      <TableCell>{comp.fecha}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{comp.cliente}</TableCell>
                      <TableCell className="font-mono text-sm">{comp.cuit}</TableCell>
                      <TableCell className={`text-right font-mono ${comp.total < 0 ? 'text-red-600' : ''}`}>
                        {formatCurrency(comp.total)}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {comp.cae || <span className="text-gray-400">-</span>}
                      </TableCell>
                      <TableCell>{getEstadoBadge(comp.estado)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-center text-sm text-gray-500 mt-4 py-4 border-t">
                Los datos mostrados son de ejemplo. Active la integración para ver comprobantes reales.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recibidos" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Comprobantes Recibidos (Vista Previa)</CardTitle>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Importar desde AFIP
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Número</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>CUIT</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>CAE</TableHead>
                    <TableHead>Validado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comprobantesRecibidos.map((comp) => (
                    <TableRow key={comp.id} className="opacity-60">
                      <TableCell>
                        <Badge variant="outline">{comp.tipo}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{comp.numero}</TableCell>
                      <TableCell>{comp.fecha}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{comp.proveedor}</TableCell>
                      <TableCell className="font-mono text-sm">{comp.cuit}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(comp.total)}</TableCell>
                      <TableCell className="font-mono text-xs">{comp.cae}</TableCell>
                      <TableCell>
                        {comp.validado ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />Validado
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-700">
                            <Clock className="w-3 h-3 mr-1" />Pendiente
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-center text-sm text-gray-500 mt-4 py-4 border-t">
                Los datos mostrados son de ejemplo. Active la integración para ver comprobantes reales.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="libros" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <CardTitle className="text-lg">Libros IVA Digital</CardTitle>
              <CardDescription>
                Generación automática de libros IVA para presentación ante AFIP
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="border-2 border-dashed border-gray-200 hover:border-[#2853B9] transition-colors cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h4 className="font-semibold mb-2">Libro IVA Ventas</h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Detalle de comprobantes emitidos con IVA débito fiscal
                    </p>
                    <Button variant="outline" disabled>
                      <Download className="w-4 h-4 mr-2" />
                      Generar
                    </Button>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-gray-200 hover:border-[#2853B9] transition-colors cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h4 className="font-semibold mb-2">Libro IVA Compras</h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Detalle de comprobantes recibidos con IVA crédito fiscal
                    </p>
                    <Button variant="outline" disabled>
                      <Download className="w-4 h-4 mr-2" />
                      Generar
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Información de Smartboat */}
      <Card className="cao-card bg-gradient-to-r from-[#2853B9]/5 to-[#1E3876]/5">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-white shadow-md flex items-center justify-center">
              <span className="text-2xl font-bold text-[#2853B9]">SB</span>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-[#1E3876]">Integración con Smartboat</h3>
              <p className="text-gray-600">
                Smartboat es nuestra plataforma de facturación electrónica homologada por AFIP. 
                Una vez configuradas las credenciales, podrá emitir y recibir comprobantes directamente desde CUBIQ.
              </p>
            </div>
            <Button className="bg-[#2853B9] hover:bg-[#1E3876]">
              <Settings className="w-4 h-4 mr-2" />
              Configurar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FacturacionElectronica;
